export const SUPPORTED_ARCHITECTURE = 'x86';
/** Hetzner bills us in EUR, so a margin only means anything on a EUR price. */
export const COST_CURRENCY = 'EUR';

export const STANDARD_TIER = 'small_business';

export interface HetznerApiPrice {
  location: string;
  price_hourly_net: string | null;
  price_hourly_gross: string | null;
  price_monthly_net: string | null;
  price_monthly_gross: string | null;
}

export interface ApiServerType {
  name: string;
  description: string;
  cores: number;
  memory: number;
  disk: number;
  cpu_type: string;
  architecture: string;
  prices: HetznerApiPrice[];
}

export interface ApiLocation {
  name: string;
  description: string;
  city: string;
  country: string;
  network_zone: string;
}

export interface CatalogData {
  server_types: ApiServerType[];
  locations: ApiLocation[];
}

export interface PriceEntry {
  id: string;
  tier: string;
  plan: string | null;
  region: string;
  // Echoed from the region so a row renders without a lookup.
  currency: string;
  effective_date: string;
  monthly_price: string;
  set_by: string;
  set_at: string | null;
  notes: string | null;
}

export type ScopedPrices = Record<string, Record<string, PriceEntry[]>>;

export interface ServerConfig {
  id: string;
  hetzner_server_type: string;
  hetzner_location: string | null;
  display_name: string;
  description: string | null;
  vcpus: number;
  memory_gb: number;
  disk_gb: number;
  is_active: boolean;
  trial_eligible: boolean;
  sort_order: number;
  prices: ScopedPrices;
  created_at: string;
  updated_at: string;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function architectureLabel(architecture: string) {
  return architecture === SUPPORTED_ARCHITECTURE ? 'amd64' : architecture;
}

export function apiMessage(err: any): string {
  const data = err?.response?.data;
  if (!data) return err?.message ?? 'Something went wrong';
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  const [field, messages] = Object.entries(data)[0] ?? [];
  if (field && Array.isArray(messages) && messages.length) return `${messages[0]}`;
  return err?.message ?? 'Something went wrong';
}

export function hetznerCost(apiType: ApiServerType | undefined, location: string | null): number | null {
  if (!apiType) return null;
  const rows = apiType.prices.filter((p) => p.price_monthly_net != null);
  const forLocation = rows.find((p) => p.location === location) ?? rows[0];
  return forLocation ? parseFloat(forLocation.price_monthly_net as string) : null;
}

export function margin(
  ourPrice: { currency: string; amount: string } | null,
  cost: number | null,
): { amount: number; percent: number } | null {
  if (!ourPrice || cost == null || ourPrice.currency !== COST_CURRENCY) return null;
  const price = parseFloat(ourPrice.amount);
  if (!Number.isFinite(price) || price <= 0) return null;
  return { amount: price - cost, percent: ((price - cost) / price) * 100 };
}

export function priceInForce(entries: PriceEntry[] | undefined): PriceEntry | null {
  if (!entries || entries.length === 0) return null;
  const now = today();
  const effective = entries
    .filter((e) => e.effective_date <= now)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0];
  if (effective) return effective;
  return [...entries].sort((a, b) => a.effective_date.localeCompare(b.effective_date))[0];
}

export function nextAfterToday(entries: PriceEntry[] | undefined): PriceEntry | null {
  if (!entries) return null;
  const now = today();
  return (
    [...entries]
      .filter((e) => e.effective_date > now)
      .sort((a, b) => a.effective_date.localeCompare(b.effective_date))[0] ?? null
  );
}

export type PriceCell =
  | { state: 'set'; row: PriceEntry; upcoming: PriceEntry | null }
  | { state: 'inherited'; row: PriceEntry }
  | { state: 'none' };

export function priceCell(prices: ScopedPrices, scope: string, region: string): PriceCell {
  const own = priceInForce(prices[scope]?.[region]);
  if (own) return { state: 'set', row: own, upcoming: nextAfterToday(prices[scope]?.[region]) };
  if (scope !== STANDARD_TIER) {
    const inherited = priceInForce(prices[STANDARD_TIER]?.[region]);
    if (inherited) return { state: 'inherited', row: inherited };
  }
  return { state: 'none' };
}

export function regionsInUse(prices: ScopedPrices): string[] {
  const found = new Set<string>();
  for (const byRegion of Object.values(prices ?? {})) {
    for (const region of Object.keys(byRegion)) found.add(region);
  }
  return [...found];
}
